import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import {
  addAccommodationGroup,
  deleteAccommodationGroup,
  fetchAccommodationGroupDetails,
  fetchAllAccommodationGroups,
  updateAccommodationGroup,
} from '../../common/api/accommodationGroupsService';
import { RootState } from '../../app/store';
import { LOADING_STATUS } from '../../common/constants';
import {
  InitialPaginationParameters,
  InitialPaginationResponse,
  PaginationParameters,
  PaginationResponse,
  PopulatePagination,
} from '../../common/types/pagination.types';

export interface AccommodationGroupListItemTranslations {
  locale: string;
  keyId: string;
  text: string;
}

export interface AccommodationGroupDetailsTranslations {
  keyId: string;
  locale: string;
  text: string;
}
export interface KeyDescriptionValue {
  Text: string;
  Value: number;
}

export interface AccommodationGroupListItem {
  id: string;
  name: string;
}

export interface AccommodationGroupDetailsData {
  id: string;
  groupLetter: string;
  hideFromLomarengasSite: boolean;
  isDeleted: boolean;
  translations: AccommodationGroupDetailsTranslations[];
  sellAccountingGroupId: string;
  purchaseAccountingGroupId: string;
  bypassPurchaseVatDeduction: boolean;
}

export interface UpdateAccommodationGroupDetails {
  id: string;
  groupLetter: string;
  hideFromLomarengasSite: boolean;
  isDeleted: boolean;
  translations: AccommodationGroupDetailsTranslations[];
  sellAccountingGroupId: string;
  purchaseAccountingGroupId: string;
  bypassPurchaseVatDeduction: boolean;
}

export interface AddAccommodationGroupDetails {
  id: string;
  groupLetter: string;
  hideFromLomarengasSite: boolean;
  isDeleted: boolean;
  translations: AccommodationGroupDetailsTranslations[];
  bypassPurchaseVatDeduction: boolean;
}

export interface AccommodationGroupQueryFilters extends PaginationParameters {
  isDeleted: boolean;
}

export interface AccommodationGroupsState {
  accommodationGroupItems: AccommodationGroupListItem[];
  accommodationGroupsStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;
  accommodationGroupDetailsStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;
  accommodationGroupDetailsData: AccommodationGroupDetailsData;
  accommodationGroupFilter: AccommodationGroupQueryFilters;
  accommodationGroupPagination: PaginationResponse;
}

const initialState: AccommodationGroupsState = {
  accommodationGroupItems: [],
  accommodationGroupsStatus: LOADING_STATUS.IDLE,
  accommodationGroupDetailsStatus: LOADING_STATUS.IDLE,
  accommodationGroupDetailsData: {
    id: '',
    groupLetter: '',
    hideFromLomarengasSite: false,
    translations: [],
    isDeleted: false,
    sellAccountingGroupId: '',
    purchaseAccountingGroupId: '',
    bypassPurchaseVatDeduction: false,
  },
  accommodationGroupFilter: {
    PageNumber: InitialPaginationParameters.PageNumber,
    PageSize: InitialPaginationParameters.PageSize,
    isDeleted: false,
  },
  accommodationGroupPagination: InitialPaginationResponse,
};

export const getAllAccommodationGroupsAsync = createAsyncThunk(
  'accommodations/getAllAccommodationGroups',
  async (
    filterCriteria: AccommodationGroupQueryFilters,
    { rejectWithValue },
  ) => {
    try {
      // Do some cheking;
      const response = await fetchAllAccommodationGroups(filterCriteria);
      return response;
    } catch (error) {
      // Use custom error response
      return rejectWithValue(error.response);
    }
  },
);

export const updateAccommodationGroupAsync = createAsyncThunk(
  'accommodations/updateAccommodationGroup',
  async (
    accommodationGroupData: UpdateAccommodationGroupDetails,
    { rejectWithValue },
  ) => {
    try {
      const response = await updateAccommodationGroup(accommodationGroupData);
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const addAccommodationGroupAsync = createAsyncThunk(
  'accommodations/addAccommodationGroup',
  async (
    accommodationGroupData: AddAccommodationGroupDetails,
    { rejectWithValue },
  ) => {
    try {
      const response = await addAccommodationGroup(accommodationGroupData);
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const deleteAccommodationGroupAsync = createAsyncThunk(
  'accommodations/updateAccommodationGroup',
  async (accommodationGroupId: string, { rejectWithValue }) => {
    try {
      const response = await deleteAccommodationGroup(accommodationGroupId);
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const getAccommodationGroupDetailsAsync = createAsyncThunk(
  'accommodations/getAccommodationGroupDetails',
  async (accommodationGroupId: string, { rejectWithValue }) => {
    try {
      const response =
        await fetchAccommodationGroupDetails(accommodationGroupId);
      return response.result;
    } catch (error) {
      // Use custom error response
      return rejectWithValue(error.response.data);
    }
  },
);

const accommodationGroupsSlice = createSlice({
  name: 'accommodationgroups',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(getAllAccommodationGroupsAsync.pending, state => {
        state.accommodationGroupsStatus = LOADING_STATUS.LOADING;
      })
      .addCase(getAllAccommodationGroupsAsync.fulfilled, (state, action) => {
        state.accommodationGroupsStatus = LOADING_STATUS.IDLE;
        state.accommodationGroupItems = action.payload.data;
        state.accommodationGroupPagination = PopulatePagination(action.payload);
      })
      .addCase(getAllAccommodationGroupsAsync.rejected, state => {
        state.accommodationGroupsStatus = LOADING_STATUS.FAILED;
      })
      .addCase(getAccommodationGroupDetailsAsync.pending, state => {
        state.accommodationGroupDetailsStatus = LOADING_STATUS.LOADING;
      })
      .addCase(getAccommodationGroupDetailsAsync.fulfilled, (state, action) => {
        state.accommodationGroupDetailsStatus = LOADING_STATUS.IDLE;
        state.accommodationGroupDetailsData = action.payload;
      })
      .addCase(getAccommodationGroupDetailsAsync.rejected, state => {
        state.accommodationGroupDetailsStatus = LOADING_STATUS.FAILED;
      });
  },
});

export const selectAccommodationGroups = (state: RootState) =>
  state.accommodationgroups.accommodationGroupItems;
export const selectAccommodationGroupsPagination = (state: RootState) =>
  state.accommodationgroups.accommodationGroupPagination;
export const selectAccommodationGroupsLoadingStatus = (state: RootState) =>
  state.accommodationgroups.accommodationGroupsStatus;
export const selectAccommodationGroupDetails = (state: RootState) =>
  state.accommodationgroups.accommodationGroupDetailsData;
export const selectAccommodationGroupDetailsLoadingStatus = (
  state: RootState,
) => state.accommodationgroups.accommodationGroupDetailsStatus;

export default accommodationGroupsSlice.reducer;
